import * as React from 'react';

/** When the browser is idle (or soon, where requestIdleCallback is missing): one step of a long list. */
const whenIdle = (fn: () => void): (() => void) => {
  const w = globalThis as typeof globalThis & {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (w.requestIdleCallback && w.cancelIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout: 100 });
    return () => w.cancelIdleCallback!(id);
  }
  const id = setTimeout(fn, 16);
  return () => clearTimeout(id);
};

/**
 * A long list, rendered a screenful first (Board #2594): the first `first` items at once, then `step`
 * more each time the browser is idle (a transition, so typing and clicks stay ahead of it) until all of
 * them are. A page with hundreds of rows shows its data after the first screenful, not after every row
 * mounted. The count only grows: a new filter, a new answer keep what is already rendered.
 */
export function useProgressive<T>(
  items: readonly T[],
  o: { first?: number; step?: number } = {},
): readonly T[] {
  const first = o.first ?? 30;
  const step = o.step ?? 60;
  const [n, setN] = React.useState(first);
  const more = n < items.length;
  React.useEffect(() => {
    if (!more) return;
    return whenIdle(() => React.startTransition(() => setN((k) => k + step)));
  }, [more, n, step]);
  return more ? items.slice(0, n) : items;
}
